import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createMigrate, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { ViewSlug } from '~/types/components';
import type { Contact } from '~/types/models';
import { restSliceMigrations } from '~/types/states';
import type { IRestSlice, IRestIndexParams } from '~/types/states';
import type { StringKeys } from '~/types/utils';

const initialState: IRestSlice<Contact> = {
  columns: {
    index: [
      'sequence',
      'firstName',
      'lastName',
      'postalCode',
      'cellPhoneNumber',
      'email',
      'lastResult',
      'groups',
      'lastUpdated',
    ],
    single: [
      'sequence',
      'firstName',
      'lastName',
      'postalCode',
      'cellPhoneNumber',
      'email',
      'lastResult',
      'groups',
      'lastUpdated',
    ],
  },
  data: [],
  error: null,
  filters: {},
  initialItem: null,
  item: null,
  loading: true,
  page: 1,
  pageSize: 10,
  sortBy: '-sequence',
  total: null,
  totalPages: null,
  with: [],
};

export const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    create: (_state, _action: PayloadAction<Partial<Contact>>) => {},
    destroy: (_state, _action: PayloadAction<Contact>) => {},
    index: (_state, _action: PayloadAction<IRestIndexParams<Contact>>) => {},
    loadParams: (_state) => {},
    massDestroy: (_state, _payload: PayloadAction<IRestSlice<Contact>['filters']>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Contact['id']; params?: IRestIndexParams<Contact> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Contact>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Contact;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Contact];
        } else {
          state.filters[k as keyof Contact] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state, _action: PayloadAction<string>) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Contact>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Contact>) => {},
  },
});

const { actions, getInitialState, reducer: contactsReducer } = contactsSlice;

const reducer = persistReducer(
  {
    key: 'CSN_Pointage:Contacts',
    migrate: createMigrate(restSliceMigrations),
    storage,
    version: 0,
    whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
  },
  contactsReducer,
);

export { actions, getInitialState, reducer };
